import { GetStaticPropsContext } from "next";

import { TreatmentService } from "@lib/strapi/services/content-types/treatment.service";
import { ConditionService } from "@lib/strapi/services/content-types/condition.service";
import { QuestionService } from "@lib/strapi/services/content-types/question.service";
import { TeamMemberService } from "@lib/strapi/services/content-types/teamMember.service";
import { PostService } from "@lib/strapi/services/content-types/post.service";
import { fetchAPI } from "@lib/strapi/fetch-api";
import { StrapiRoutes } from "@lib/strapi/routes";
import { fetchMenu } from "@lib/frontend/menus";
import { fetchGoogleReviews, GoogleReviewsData } from "@lib/frontend/google-reviews";
import { StrapiMenus } from "@lib/strapi/menus";
import type { Menu } from "@lib/menu";

/**
 * Sections whose content is not authored in the page but fetched from a
 * collection: the treatments grid, the conditions list, the FAQ accordion, the
 * team, the blog strip.
 *
 * The page seed says WHICH section goes where and how it is configured; this
 * resolves WHAT it renders. Keeping the two apart is what lets an editor add a
 * treatment and have it appear on the homepage without touching the page.
 *
 * Everything is fetched server-side in getStaticProps. Nothing here runs in the
 * browser, so the API token never reaches the client.
 */

export const NOT_FOUND = Symbol("not-found");

type Zone = { __component?: string; [k: string]: unknown }[] | undefined;

const componentsIn = (...zones: Zone[]): Set<string> => {
  const found = new Set<string>();
  for (const zone of zones)
    for (const item of zone ?? [])
      if (item?.__component) found.add(item.__component as string);
  return found;
};

export interface SectionsData {
  treatments?: unknown[];
  homepageTreatments?: unknown[];
  conditions?: unknown[];
  questions?: unknown[];
  homepageQuestions?: unknown[];
  team?: unknown[];
  posts?: unknown[];
  /** seeded sidebar menus, keyed by the menu-section enum */
  sidebarMenus?: Record<string, Menu | null>;
  /** live Places aggregate + cards (D51); null when the key is absent */
  googleReviews?: GoogleReviewsData | null;
}

/**
 * Fetch only what the page's zones actually reference.
 *
 * The alternative — fetching everything for every page — costs five extra
 * round trips per page at build time and grows with the model. With ~30 static
 * pages across two locales that is the difference between a fast build and a
 * slow one, for data most pages never render.
 */
export const getSectionsData = async (
  context: GetStaticPropsContext,
  page: { contentSections?: Zone; asideSections?: Zone; bottomSections?: Zone; headingZone?: Zone }
): Promise<SectionsData> => {
  const locale = context.locale ?? "el";
  const used = componentsIn(
    page.contentSections,
    page.asideSections,
    page.bottomSections,
    page.headingZone
  );
  const data: SectionsData = {};

  if (used.has("sections.treatments-section")) {
    const all = await TreatmentService.fetchAll(null, locale);
    data.treatments = all;
    data.homepageTreatments = (all ?? []).filter(
      (t: any) => t?.featuredInHomepageCarousel
    );
  }

  if (used.has("sections.conditions-section") || used.has("sections.related-section")) {
    data.conditions = await ConditionService.fetchAll(null, locale);
  }

  if (used.has("sections.faq-section")) {
    const all = await QuestionService.fetchAll(null, locale);
    data.questions = all;
    data.homepageQuestions = (all ?? []).filter((q: any) => q?.featuredOnHomepage);
  }

  if (used.has("sections.team-section")) {
    data.team = await TeamMemberService.fetchAll(null, locale);
  }

  if (used.has("sections.posts-section")) {
    const posts = await PostService.fetchAll(null, locale);

    // Reading time for the card meta line («4 λεπτά ανάγνωση»). The parsed
    // DTO carries compiled MDX, not the raw markdown, so the word count comes
    // from one extra slug+content fetch — computed HERE (hand-written file)
    // rather than in the generated PostService, which a regen would revert.
    try {
      const raw = (await fetchAPI(
        StrapiRoutes.posts(),
        { locale, fields: ["slug", "content"], pagination: { pageSize: 500 } },
        process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN
      )) as { data?: { slug?: string; content?: string }[] };
      const minutesBySlug = new Map(
        (raw.data ?? []).map((p) => {
          const words = String(p.content ?? "").split(/\s+/).filter(Boolean).length;
          // 180 wpm — slightly below the usual 200 for medical Greek copy
          return [p.slug, Math.max(1, Math.round(words / 180))];
        })
      );
      for (const post of posts ?? []) {
        (post as { readingMinutes?: number }).readingMinutes =
          minutesBySlug.get((post as { slug?: string }).slug);
      }
    } catch {
      // meta line simply omits the minutes — never worth failing the build
    }

    data.posts = posts;
  }

  // menu-section too: the sidebar booking card carries the minimal
  // aggregate strip on every entity page (owner, 2026-09-08)
  if (used.has("sections.google-reviews-section") || used.has("sections.menu-section")) {
    data.googleReviews = await fetchGoogleReviews(locale);
  }

  if (used.has("sections.menu-section")) {
    const [legal, clinical] = await Promise.all([
      fetchMenu(context, StrapiMenus.sidebarLegal),
      fetchMenu(context, StrapiMenus.sidebarClinical),
    ]);
    data.sidebarMenus = { legal, clinical };
  }

  return data;
};
